import afterIntercomHasLoaded from 'root/utils/afterIntercomHasLoaded';
import { handleSandboxFormVisibility } from '../ui/consentPanelUI';
import isLightboxFormSandbox from '../utils/isLightboxFormSandbox';

export default class FormSandboxUI {
    #document: Document;
    #bodyEl: HTMLElement;
    #siteContent: HTMLElement | null;
    escapeAbortController: AbortController;
    readonly #lightboxEl: HTMLElement;
    readonly #innerLightboxEl: HTMLElement;

    constructor() {
        this.#document = document;
        this.#bodyEl = document.body;
        this.#siteContent = document.querySelector<HTMLElement>('#page');
        this.#lightboxEl = this.#createLightboxEl();
        this.#innerLightboxEl = this.#createInnerEl();
        this.escapeAbortController = new AbortController();
    }

    get getLightboxEl() {
        return this.#innerLightboxEl;
    }

    #createLightboxEl() {
        const lightboxEl = document.querySelector<HTMLElement>('.js-lightbox-wrapper');
        const isSandbox = isLightboxFormSandbox(lightboxEl);
        if (lightboxEl && isSandbox) {
            return lightboxEl; // this is to prevent creating multiple lightbox elements
        } else if (lightboxEl && !isSandbox) {
            lightboxEl.remove();
        }

        const lightboxElWrapper = document.createElement('div');
        lightboxElWrapper.classList.add(
            'js-lightbox-wrapper',
            'js-leadform-form-sandbox',
            'js-leadform-lightbox-wrapper'
        );
        this.#prepSiteContentForSandbox();
        return this.#bodyEl.insertBefore(lightboxElWrapper, this.#bodyEl.firstChild);
    }

    #createInnerEl() {
        const previousInnerEl = this.#lightboxEl.querySelector<HTMLElement>(
            '.js-leadform-form-sandbox__modal'
        );
        if (previousInnerEl) return previousInnerEl;
        const innerEl = document.createElement('div');
        innerEl.classList.add('js-leadform-form-sandbox__modal', 'tw:flex');
        this.#lightboxEl.appendChild(innerEl);
        return innerEl;
    }

    clickHandler() {
        this.#show();
        this.#addClosingButton();
        this.#resetLightboxContent();
    }

    #show() {
        this.#bodyEl.classList.add('js-show-lightbox', 'js-show-form-sandbox');
        this.#lightboxEl.style.display = 'flex';
        this.#toggleSiteBlurAndInert();
        afterIntercomHasLoaded(this.#toggleIntercomDisplay);
        this.#document.addEventListener('keydown', this.handleEscapeKey, {
            signal: this.escapeAbortController.signal,
        });
    }

    #hideConsentPanel() {
        const consentPanel = this.#lightboxEl.querySelector<HTMLElement>(
            '[data-form-consent-section-inline-wrapper]'
        );
        if (consentPanel && consentPanel.classList.contains('gb-flex')) {
            consentPanel.classList.replace('gb-flex', 'gb-hide');
            consentPanel.classList.replace(
                'tw:animate-fade-in-fast',
                'tw:animate-fade-out-fast'
            );
            this.#bodyEl.classList.remove('tw:overflow-hidden');
            handleSandboxFormVisibility(
                this.#lightboxEl.querySelector('form') as HTMLFormElement,
                'visible'
            );
        }
    }

    #hide() {
        this.#hideConsentPanel();
        this.#bodyEl.classList.remove('js-show-lightbox', 'js-show-form-sandbox');
        this.#lightboxEl.style.display = 'none';
        this.#toggleSiteBlurAndInert();
        this.escapeAbortController.abort();
        this.escapeAbortController = new AbortController();
        const lightboxClosed = new CustomEvent('lightboxClosed');
        document.body.dispatchEvent(lightboxClosed);
        afterIntercomHasLoaded(() => this.#toggleIntercomDisplay(false));
    }

    #resetLightboxContent() {
        const isCloseBtn =
            (this.#innerLightboxEl.firstChild as HTMLElement | undefined)?.getAttribute(
                'data-sandbox-modal-close-button'
            ) !== null;
        while (isCloseBtn && this.#innerLightboxEl.children.length > 1) {
            if (this.#innerLightboxEl.lastChild) {
                this.#innerLightboxEl.removeChild(this.#innerLightboxEl.lastChild);
            }
        }
    }

    #addClosingButton() {
        const closeBtn = document.createElement('button');
        closeBtn.classList.add('js-leadform-form-sandbox__modal__close-btn');
        closeBtn.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <rect width="24" height="24"/>
                <path d="M10.6122 11.9999L6.3999 7.78758L7.78757 6.3999L11.9999 10.6122L16.2122 6.3999L17.5999 7.78758L13.3876 11.9999L17.5999 16.2122L16.2122 17.5999L11.9999 13.3876L7.7876 17.5999L6.39993 16.2122L10.6122 11.9999Z" fill="#191513"/>
            </svg>
        `;
        closeBtn.setAttribute('type', 'button');
        closeBtn.setAttribute('aria-label', 'Close pop-up');
        closeBtn.setAttribute('data-sandbox-modal-close-button', '');
        this.#innerLightboxEl.insertBefore(
            closeBtn,
            this.#innerLightboxEl.firstElementChild
        );
        closeBtn.addEventListener('click', this.#hide.bind(this));
    }

    handleEscapeKey = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
            const activeTooltips = document.querySelectorAll('.js-show-tooltip-dialog');
            if (activeTooltips.length > 0) return;

            if (this.#lightboxEl.style.display === 'flex') {
                this.#hide();
                return;
            }
        }
    };

    #prepSiteContentForSandbox() {
        const transitionClasses = ['tw:transition-[filter]', 'tw:duration-150'];
        if (!this.#siteContent) return;
        this.#siteContent.classList.add(...transitionClasses);
    }

    #toggleSiteBlurAndInert() {
        if (!this.#siteContent) return;
        const blurClasses = ['tw:blur-[25px]', 'tw:overflow-hidden'];
        this.#siteContent.classList.toggle(blurClasses[0]);
        this.#siteContent.classList.toggle(blurClasses[1]);
        const isInert = this.#siteContent.attributes.getNamedItem('inert');
        if (isInert) {
            this.#siteContent.removeAttribute('inert');
        } else {
            this.#siteContent.setAttribute('inert', '');
        }
    }

    #toggleIntercomDisplay(hide = true) {
        const intercom = document.querySelector<HTMLElement>(
            '.intercom-lightweight-app, .intercom-app, #intercom-container'
        );
        const toggle = hide ? 'none' : 'block';
        intercom && (intercom.style.display = toggle);
    }
}
